// ─── AuditPdfDocument.jsx ────────────────────────────────────────────────────
// Redesigned to mirror design_handoff_geo_page_audit_report/: one page per
// section (Übersicht, Inhalt, Autorität, Technik, Nutzer), dropping the old
// "Inhaltsverbesserungen" and "Gescrapte Inhalte" sections entirely.
import { Document, Page, Text, View, StyleSheet, Link, pdf, Svg, Circle, Path } from "@react-pdf/renderer";

const scoreColor  = (s) => s >= 80 ? "#16a34a" : s >= 50 ? "#f59e0b" : "#dc2626";
const scoreBg     = (s) => s >= 80 ? "#f0fdf4" : s >= 50 ? "#fffbeb" : "#fef2f2";
const fmtDate     = (iso) => iso ? new Date(iso).toLocaleDateString("de-CH") : "";

// ─── Multilingual label maps ──────────────────────────────────────────────────
const NAV_SHORT = {
  de: { contentQuality: "Inhalt",    authority: "Autorität", technicalSeo: "Technik",   engagement: "Nutzer"      },
  en: { contentQuality: "Content",   authority: "Authority", technicalSeo: "Technical", engagement: "Engagement" },
  fr: { contentQuality: "Contenu",   authority: "Autorité",  technicalSeo: "Technique", engagement: "Engagement" },
  it: { contentQuality: "Contenuto", authority: "Autorità",  technicalSeo: "Tecnica",   engagement: "Engagement" },
};
const SUBTITLE = {
  de: { contentQuality: "Inhaltsqualität & Relevanz", authority: "Autorität & Vertrauenswürdigkeit", technicalSeo: "Technische Performance & Web Vitals (Lighthouse / PSI)", engagement: "Nutzerengagement" },
  en: { contentQuality: "Content Quality & Relevance", authority: "Authority & Trustworthiness", technicalSeo: "Technical Performance & Web Vitals (Lighthouse / PSI)", engagement: "User Engagement" },
  fr: { contentQuality: "Qualité & pertinence du contenu", authority: "Autorité & fiabilité", technicalSeo: "Performance technique & Web Vitals (Lighthouse / PSI)", engagement: "Engagement utilisateur" },
  it: { contentQuality: "Qualità e rilevanza dei contenuti", authority: "Autorità e affidabilità", technicalSeo: "Performance tecnica e Web Vitals (Lighthouse / PSI)", engagement: "Coinvolgimento utenti" },
};
const BREAKDOWN_LABELS = {
  de: {
    "Alt Text": "Alt-Texte", "Meta Tags": "Meta-Tags", "Accessibility": "Barrierefreiheit",
    "Page Structure": "Seitenstruktur", "Heading Structure": "Überschriftenstruktur",
    "Natural Language": "Natürliche Sprache", "Technical Details": "Technische Details",
    "Accuracy And Currency": "Aktualität & Genauigkeit", "Comprehensive Coverage": "Vollständige Abdeckung",
    "External References": "Externe Referenzen", "Testimonials": "Kundenbewertungen",
    "Legal Compliance": "Rechtliche Konformität", "Social Presence": "Social-Media-Präsenz",
    "Multimedia": "Multimedia", "Call To Action": "Call-to-Action",
    "Contact Options": "Kontaktmöglichkeiten", "Structured Content": "Strukturierter Inhalt", "Language Page": "Sprachseite",
    "Leistung": "Leistung", "Barrierefreiheit": "Barrierefreiheit", "Best Practices": "Best Practices", "Seo": "SEO",
  },
  en: {
    "Leistung": "Performance", "Barrierefreiheit": "Accessibility", "Best Practices": "Best Practices", "Seo": "SEO",
  },
  fr: {
    "Leistung": "Performance", "Barrierefreiheit": "Accessibilité", "Best Practices": "Bonnes pratiques", "Seo": "SEO",
  },
  it: {
    "Leistung": "Prestazioni", "Barrierefreiheit": "Accessibilità", "Best Practices": "Best Practices", "Seo": "SEO",
  },
};

const UI = {
  de: {
    eyebrowOverview: "Übersicht",
    metaLabel: "GEO Audit", scoreLabel: "AI Search Readiness Score", areasEyebrow: "Die 4 Bewertungsbereiche",
    checkTitle: "Title", checkMetaDesc: "Meta Desc.", checkH1: "H1", checkAltText: "Alt Texte", checkContact: "Kontakt", checkSocial: "Social",
    areaLabel: "Bewertungsbereich", findings: "Erkenntnisse", recommendations: "Empfehlungen",
    psiButton: "Live-Check auf PSI starten", vitalsPassed: "Core Web Vitals: Bestanden", vitalsFailed: "Core Web Vitals: Nicht bestanden", vitalsUnknown: "Core Web Vitals: Nicht verfügbar",
    footer: "GEO Audit", page: (n, total) => `Seite ${n} / ${total}`,
  },
  en: {
    eyebrowOverview: "Overview",
    metaLabel: "GEO Audit", scoreLabel: "AI Search Readiness Score", areasEyebrow: "The 4 Scoring Areas",
    checkTitle: "Title", checkMetaDesc: "Meta Desc.", checkH1: "H1", checkAltText: "Alt Text", checkContact: "Contact", checkSocial: "Social",
    areaLabel: "Scoring Area", findings: "Findings", recommendations: "Recommendations",
    psiButton: "Run live check on PSI", vitalsPassed: "Core Web Vitals: Passed", vitalsFailed: "Core Web Vitals: Failed", vitalsUnknown: "Core Web Vitals: Not available",
    footer: "GEO Audit", page: (n, total) => `Page ${n} / ${total}`,
  },
  fr: {
    eyebrowOverview: "Aperçu",
    metaLabel: "Audit GEO", scoreLabel: "Score de préparation à la recherche IA", areasEyebrow: "Les 4 domaines d'évaluation",
    checkTitle: "Titre", checkMetaDesc: "Méta desc.", checkH1: "H1", checkAltText: "Textes alt", checkContact: "Contact", checkSocial: "Réseaux sociaux",
    areaLabel: "Domaine d'évaluation", findings: "Résultats", recommendations: "Recommandations",
    psiButton: "Lancer une vérification en direct sur PSI", vitalsPassed: "Core Web Vitals : Réussi", vitalsFailed: "Core Web Vitals : Échoué", vitalsUnknown: "Core Web Vitals : Non disponible",
    footer: "Audit GEO", page: (n, total) => `Page ${n} / ${total}`,
  },
  it: {
    eyebrowOverview: "Panoramica",
    metaLabel: "Audit GEO", scoreLabel: "Punteggio di preparazione alla ricerca AI", areasEyebrow: "Le 4 aree di valutazione",
    checkTitle: "Titolo", checkMetaDesc: "Meta desc.", checkH1: "H1", checkAltText: "Testi alt", checkContact: "Contatto", checkSocial: "Social",
    areaLabel: "Area di valutazione", findings: "Risultati", recommendations: "Raccomandazioni",
    psiButton: "Avvia verifica live su PSI", vitalsPassed: "Core Web Vitals: Superato", vitalsFailed: "Core Web Vitals: Non superato", vitalsUnknown: "Core Web Vitals: Non disponibile",
    footer: "Audit GEO", page: (n, total) => `Pagina ${n} / ${total}`,
  },
};

const camelToTitle = (s) => s.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase());
const getLang = (l) => (["de", "en", "fr", "it"].includes(l) ? l : "de");
const makeTbd = (lang) => (key) => (BREAKDOWN_LABELS[lang] || {})[camelToTitle(key)] || camelToTitle(key);

const CAT_ORDER = ["contentQuality", "authority", "technicalSeo", "engagement"];

const safe = (str) => (str || "")
  .replace(/[◆•◦▸▹]/g, "-")
  .replace(/→/g, ">")
  .replace(/[✓✔☑]/g, "OK")
  .replace(/[✗✘☒]/g, "X")
  .replace(/[—–]/g, "-")
  .replace(/[""„]/g, '"')
  .replace(/[''‚]/g, "'");

const recText = (r) => (typeof r === "object" ? (r.title ? `${r.title}: ${r.text}` : r.text) : r);

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  page: {
    fontFamily: "Helvetica", fontSize: 10, color: "#1e293b",
    backgroundColor: "#ffffff",
    paddingTop: 40, paddingBottom: 52, paddingLeft: 48, paddingRight: 48,
  },

  eyebrow:      { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#2563eb", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 },
  metaLine:     { fontSize: 8.5, color: "#94a3b8", marginBottom: 24 },

  // Overview hero
  // alignItems: "flex-start" (not "center") — with a fixed-height donut next to
  // a variable-height, possibly-multi-line summary text, react-pdf's layout
  // engine can under-report the row's total height when center-aligned,
  // causing the next section to render too early and overlap the summary.
  heroRow:      { flexDirection: "row", alignItems: "flex-start", paddingBottom: 20, marginBottom: 20 },
  heroCircleCol:{ alignItems: "center", width: 100, marginRight: 20 },
  heroTitle:    { fontSize: 12, fontFamily: "Helvetica-Bold", color: "#0f172a", marginBottom: 6 },
  heroSummary:  { fontSize: 9.5, color: "#475569", lineHeight: 1.6 },

  areasEyebrow: { fontSize: 8.5, fontFamily: "Helvetica-Bold", color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 },
  areaGrid:     { flexDirection: "row", flexWrap: "wrap" },
  areaCard:     { width: "23.5%", marginRight: "2%", marginBottom: 10, backgroundColor: "#ffffff", borderRadius: 10, borderWidth: 1, borderColor: "#e2e8f0", padding: 12 },
  areaLbl:      { fontSize: 7.5, fontFamily: "Helvetica-Bold", color: "#94a3b8", textTransform: "uppercase", marginBottom: 8 },
  areaScore:    { fontSize: 18, fontFamily: "Helvetica-Bold", marginBottom: 8 },

  // Detail page header
  detailHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  detailLeft:   { flexDirection: "row", alignItems: "center" },
  detailCircleCol: { alignItems: "center", width: 90, marginRight: 18 },
  sectionH1:    { fontSize: 22, fontFamily: "Helvetica-Bold", color: "#0f172a" },
  sectionSub:   { fontSize: 9.5, color: "#64748b", marginTop: 3 },
  psiBtn:       { backgroundColor: "#0f172a", borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
  psiBtnTxt:    { fontSize: 8.5, fontFamily: "Helvetica-Bold", color: "#ffffff" },

  // Metric cards row
  metricGrid:   { flexDirection: "row", flexWrap: "wrap", marginBottom: 16 },
  metricCard:   { width: "23.5%", marginRight: "2%", marginBottom: 10, backgroundColor: "#ffffff", borderRadius: 10, borderWidth: 1, borderColor: "#e2e8f0", padding: 10 },
  metricLbl:    { fontSize: 7, color: "#64748b", fontFamily: "Helvetica-Bold", marginBottom: 6 },
  metricScore:  { fontSize: 14, fontFamily: "Helvetica-Bold", marginBottom: 6 },
  barTrack:     { height: 4, backgroundColor: "#f1f5f9", borderRadius: 2 },
  barFill:      { height: 4, borderRadius: 2 },

  // Vitals strip
  vitalsStrip:  { flexDirection: "row", alignItems: "center", borderRadius: 10, borderWidth: 1, paddingVertical: 10, paddingHorizontal: 14, marginBottom: 20 },
  vitalsLabel:  { fontSize: 8.5, fontFamily: "Helvetica-Bold", marginRight: 14 },
  vitalItem:    { flexDirection: "row", marginRight: 14 },
  vitalKey:     { fontSize: 8, color: "#64748b", fontFamily: "Helvetica-Bold", marginRight: 4 },
  vitalVal:     { fontSize: 8, color: "#0f172a", fontFamily: "Helvetica-Bold" },

  // Insights / Recs
  insightsGrid: { flexDirection: "row", marginTop: 4 },
  insightCard:  { flex: 1, marginRight: 10, backgroundColor: "#f8fafc", borderRadius: 10, borderWidth: 1, borderColor: "#e2e8f0", padding: 14 },
  recCard:      { flex: 1, backgroundColor: "#eff6ff", borderRadius: 10, borderWidth: 1, borderColor: "#dbeafe", padding: 14 },
  blockHdr:     { fontSize: 8, fontFamily: "Helvetica-Bold", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 },
  bulletRow:    { flexDirection: "row", marginBottom: 6 },
  bulletMark:   { fontSize: 8, width: 10 },
  bulletTxt:    { fontSize: 8.5, color: "#334155", flex: 1, lineHeight: 1.4 },

  footer:       { position: "absolute", bottom: 18, left: 48, right: 48, flexDirection: "row", justifyContent: "space-between", borderTopWidth: 1, borderTopColor: "#e2e8f0", paddingTop: 6 },
  footerTxt:    { fontSize: 7.5, color: "#94a3b8" },
});

// ─── ArcCircle — SVG partial arc ring matching score % ───────────────────────
function ArcCircle({ score, size = 54 }) {
  const s  = Math.max(0, Math.min(100, score ?? 0));
  const c  = scoreColor(s);
  const bg = scoreBg(s);
  const sw = size <= 60 ? 4 : 6;
  const r  = (size / 2) - sw;
  const cx = size / 2;
  const cy = size / 2;
  const fs  = Math.round(size * 0.24);
  const fss = Math.round(size * 0.11);

  const arcPath = (() => {
    if (s <= 0)  return null;
    if (s >= 100) return null;
    const startDeg = -90;
    const endDeg   = startDeg + (s / 100) * 360;
    const a1 = startDeg * Math.PI / 180;
    const a2 = endDeg   * Math.PI / 180;
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2);
    const y2 = cy + r * Math.sin(a2);
    const large = s > 50 ? 1 : 0;
    return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  })();

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle cx={cx} cy={cy} r={r} fill={bg} stroke="#e2e8f0" strokeWidth={sw} />
        {s >= 100
          ? <Circle cx={cx} cy={cy} r={r} fill="none" stroke={c} strokeWidth={sw} />
          : arcPath ? <Path d={arcPath} stroke={c} strokeWidth={sw} fill="none" /> : null
        }
      </Svg>
      <View style={{ position: "absolute", top: 0, left: 0, width: size, height: size, alignItems: "center", justifyContent: "center" }}>
        <Text style={{ fontSize: fs, fontFamily: "Helvetica-Bold", color: c, lineHeight: 1 }}>
          {s}<Text style={{ fontSize: fss, fontFamily: "Helvetica", opacity: 0.6 }}>/100</Text>
        </Text>
      </View>
    </View>
  );
}

function MetricCard({ label, score }) {
  const c = scoreColor(score || 0);
  return (
    <View style={S.metricCard}>
      <Text style={S.metricLbl}>{label}</Text>
      <Text style={[S.metricScore, { color: c }]}>{score ?? "—"}</Text>
      <View style={S.barTrack}>
        <View style={[S.barFill, { width: `${Math.min(score || 0, 100)}%`, backgroundColor: c }]} />
      </View>
    </View>
  );
}

function InsightsRecs({ cat, ui }) {
  const hasF = cat?.findings?.length > 0;
  const hasR = cat?.recommendations?.length > 0;
  if (!hasF && !hasR) return null;
  return (
    <View style={S.insightsGrid}>
      <View style={S.insightCard}>
        <Text style={[S.blockHdr, { color: "#0f172a" }]}>{ui.findings}</Text>
        {(cat.findings || []).map((f, i) => (
          <View key={i} style={S.bulletRow}>
            <Text style={[S.bulletMark, { color: "#94a3b8" }]}>-</Text>
            <Text style={S.bulletTxt}>{safe(f)}</Text>
          </View>
        ))}
      </View>
      <View style={S.recCard}>
        <Text style={[S.blockHdr, { color: "#1d4ed8" }]}>{ui.recommendations}</Text>
        {(cat.recommendations || []).map((r, i) => (
          <View key={i} style={S.bulletRow}>
            <Text style={[S.bulletMark, { color: "#2563eb" }]}>{">"}</Text>
            <Text style={S.bulletTxt}>{safe(recText(r))}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function PageFooter({ domain, date, ui }) {
  return (
    <View style={S.footer} fixed>
      <Text style={S.footerTxt}>{ui.footer} – {domain} – {date}</Text>
      <Text style={S.footerTxt} render={({ pageNumber, totalPages }) => ui.page(pageNumber, totalPages)} />
    </View>
  );
}

// ─── Overview Page ────────────────────────────────────────────────────────────
function OverviewPage({ audit, domain, date, score, ui, lang }) {
  const cats = CAT_ORDER
    .filter(k => audit.categories?.[k])
    .map(k => ({ key: k, ...audit.categories[k] }));
  return (
    <Page size="A4" style={S.page}>
      <Text style={S.eyebrow}>{ui.eyebrowOverview}</Text>
      <Text style={S.metaLine}>{ui.metaLabel} · {domain} · {date}</Text>

      <View style={S.heroRow}>
        <View style={S.heroCircleCol}>
          <ArcCircle score={score} size={100} />
        </View>
        {/* Explicit width (A4 usable width 499pt - donut col 120pt) instead of
            flex:1 — react-pdf can miscalculate a Text node's wrapped height when
            it has to resolve a flex-based width first, which is what caused the
            overlap with the section below. */}
        <View style={{ width: 379 }}>
          <Text style={S.heroTitle}>{ui.scoreLabel}</Text>
          <Text style={S.heroSummary}>{safe(audit.summary)}</Text>
        </View>
      </View>

      <Text style={S.areasEyebrow}>{ui.areasEyebrow}</Text>
      <View style={S.areaGrid}>
        {cats.map((cat) => {
          const c = scoreColor(cat.score);
          return (
            <View key={cat.key} style={S.areaCard}>
              <Text style={S.areaLbl}>{NAV_SHORT[lang][cat.key]}</Text>
              <Text style={[S.areaScore, { color: c }]}>{cat.score}</Text>
              <View style={S.barTrack}>
                <View style={[S.barFill, { width: `${cat.score}%`, backgroundColor: c }]} />
              </View>
            </View>
          );
        })}
      </View>

      <PageFooter domain={domain} date={date} ui={ui} />
    </Page>
  );
}

// ─── Detail Page (Inhalt / Autorität / Technik / Nutzer) ─────────────────────
function DetailPage({ catKey, cat, idx, domain, date, ui, lang, psiUrl }) {
  const isTechnik = catKey === "technicalSeo";
  const breakdown = cat?.breakdown || {};
  const isPsiBreakdown = "leistung" in breakdown;
  const vitals = cat?.vitals;
  const psiReportUrl = cat?.psiReportUrl || psiUrl;

  const vitalsBg     = vitals == null ? "#f8fafc" : vitals.passed ? "#f0fdf4" : "#fef2f2";
  const vitalsBorder = vitals == null ? "#e2e8f0" : vitals.passed ? "#bbf7d0" : "#fecaca";
  const vitalsColor  = vitals == null ? "#64748b" : vitals.passed ? "#16a34a" : "#dc2626";
  const vitalsText   = vitals == null ? ui.vitalsUnknown : vitals.passed ? ui.vitalsPassed : ui.vitalsFailed;

  return (
    <Page size="A4" style={S.page}>
      <Text style={S.eyebrow}>{ui.areaLabel} {idx + 1} / 4</Text>

      <View style={S.detailHeader}>
        <View style={S.detailLeft}>
          <View style={S.detailCircleCol}>
            <ArcCircle score={cat?.score} size={90} />
          </View>
          <View>
            <Text style={S.sectionH1}>{NAV_SHORT[lang][catKey]}</Text>
            <Text style={S.sectionSub}>{SUBTITLE[lang][catKey]}</Text>
          </View>
        </View>
        {isTechnik && psiReportUrl && (
          <Link src={psiReportUrl} style={S.psiBtn}>
            <Text style={S.psiBtnTxt}>{ui.psiButton}</Text>
          </Link>
        )}
      </View>

      <View style={S.metricGrid}>
        {Object.entries(breakdown).map(([k, v]) => (
          <MetricCard key={k} label={makeTbd(lang)(k)} score={v} />
        ))}
      </View>

      {isTechnik && isPsiBreakdown && (
        <View style={[S.vitalsStrip, { backgroundColor: vitalsBg, borderColor: vitalsBorder }]}>
          <Text style={[S.vitalsLabel, { color: vitalsColor }]}>{vitalsText}</Text>
          {vitals && (
            <>
              <View style={S.vitalItem}><Text style={S.vitalKey}>LCP</Text><Text style={S.vitalVal}>{vitals.lcp ?? "—"}</Text></View>
              <View style={S.vitalItem}><Text style={S.vitalKey}>INP</Text><Text style={S.vitalVal}>{vitals.inp ?? "—"}</Text></View>
              <View style={S.vitalItem}><Text style={S.vitalKey}>CLS</Text><Text style={S.vitalVal}>{vitals.cls ?? "—"}</Text></View>
            </>
          )}
        </View>
      )}

      <InsightsRecs cat={cat} ui={ui} />

      <PageFooter domain={domain} date={date} ui={ui} />
    </Page>
  );
}

// ─── Main Document ────────────────────────────────────────────────────────────
function AuditPdfDocument({ audit, lang: rawLang }) {
  if (!audit) return null;

  const lang = getLang(rawLang);
  const ui   = UI[lang];

  const domain = audit.url?.replace(/https?:\/\//, "").split("/")[0] || audit.url || "";
  const date   = fmtDate(audit.auditDate || audit.created_at);
  const score  = audit.overallScore ?? audit.overall_score;

  const psiUrl = `https://pagespeed.web.dev/analysis?url=${encodeURIComponent(audit.url || "")}`;

  return (
    <Document title={`GEO Audit – ${domain}`} author="AI Visibility Tool" creator="georev.ai">
      <OverviewPage audit={audit} domain={domain} date={date} score={score} ui={ui} lang={lang} />
      {CAT_ORDER.filter(k => audit.categories?.[k]).map((catKey, idx) => (
        <DetailPage
          key={catKey}
          catKey={catKey}
          cat={audit.categories[catKey]}
          idx={idx}
          domain={domain}
          date={date}
          ui={ui}
          lang={lang}
          psiUrl={psiUrl}
        />
      ))}
    </Document>
  );
}

// ─── Exports ──────────────────────────────────────────────────────────────────
export async function downloadAuditPdf(audit, lang = "de") {
  const domain = audit?.url?.replace(/https?:\/\//, "").split("/")[0] || "audit";
  const blob   = await pdf(<AuditPdfDocument audit={audit} lang={lang} />).toBlob();
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement("a");
  a.href = url; a.download = `geo-audit-${domain}.pdf`; a.click();
  URL.revokeObjectURL(url);
}

// Opens the rendered PDF in a new browser tab. `targetWindow` should be a
// window reference from a *synchronous* `window.open("", "_blank")` call made
// directly in the click handler — since generating the PDF blob is async,
// calling window.open() only after it resolves would be blocked by most
// browsers' popup blockers (they only reliably allow the synchronous case).
export async function openAuditPdf(audit, lang = "de", targetWindow = null) {
  const blob = await pdf(<AuditPdfDocument audit={audit} lang={lang} />).toBlob();
  const url  = URL.createObjectURL(blob);
  if (targetWindow && !targetWindow.closed) targetWindow.location.href = url;
  else window.open(url, "_blank");
}

export async function getPdfBase64(audit, lang = "de") {
  const blob = await pdf(<AuditPdfDocument audit={audit} lang={lang} />).toBlob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror   = reject;
    reader.readAsDataURL(blob);
  });
}

export default AuditPdfDocument;
